import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import ListFilters from "@/common/components/ListFilters";
import { ShowHeader } from "@/common/components/ShowHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { tefProviders } from "@/features/establishments/constants/tefModels.constant";
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany";
import { Building2Icon, SearchIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import { tefConfigurationProductsTableColumns } from "./constants/tefConfigurationProductsTableColumns.constant";
import TefConfigurationProductCreateModal from "../Create";
import Skeleton from "@/common/components/Skeleton";
import { useProductCategories } from "@/common/hooks/queries/useProductCategories";
import { Mapper } from "@/common/services/mapper";
import { ProductCategoryData } from "@/common/types/productCategory.type";
import { useTefProductCodes } from "@/features/establishments/hooks/tefConfiguration/useTefProductCodes";
import { TefProductCodesTransformer } from "@/features/establishments/services/tefConfiguration/tefProductCodesTransformer";

const TefConfigurationShow = () => {
  const { establishmentId, tefProvider } = useParams();

  const { data: establishment, isPending: establishmentIsPending } = useEstablishmentCompany(establishmentId || '');

  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchProductCategories, setSearchProductCategories] = useDebounce(filterParams.search || '');

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: searchProductCategories },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const {
    data: tefProductCodes,
    meta: tefProductCodesMeta,
    isPending: tefProductCodesIsPending,
    error: tefProductCodesError = {} as any,
    refetch: refetchTefProductCodes,
  } = useTefProductCodes({
    filters: {
      search,
      page: Number(filterParams.page) || 1,
      companyId: establishmentId,
      provider: tefProvider,
      productCategoryId: filterParams.product_category?.value,
    },
    mapper: Mapper.mapWithTransformer({ transformer: TefProductCodesTransformer.tableTransformer() })
  });

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={tefProductCodesError?.status}
      refetchFunction={refetchTefProductCodes}
      notFoundFallback={`/establishments/${establishmentId}/tef-configurations`}
      notFoundButtonText={'Ir para Configurações TEF'}
    >
      <ShowHeader
        goBack
        goBackProps={{
          fallback: `/establishments/${establishmentId}/tef-configurations`,
          searchLastRouteFromFallback: true,
        }}
        LogoPlaceholder={(
          <ImageWithPlaceholder
            placeholderIcon={Building2Icon}
            url={establishment?.general_information?.company_logo?.url}
            className="size-6"
            iconClassName="size-4"
          />
        )}
        titleClassName="font-normal text-lg text-white-400"
        subtitleClassName="font-normal text-lg"
        title={establishmentIsPending ? <Skeleton className="w-36" /> : establishment?.general_information?.fantasy_name}
        subtitle={establishmentIsPending ? '' : `Tabela de códigos ${tefProviders[tefProvider as keyof typeof tefProviders].label}`}
        buttons={(
          <TefConfigurationProductCreateModal
            provider={tefProvider}
            establishment={establishment}
          />
        )}
      />

      <div className="h-[calc((100vh-88px-2rem-1.5rem))] flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <TextField
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-xl"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value);
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value, page: null }));
            }}
            value={filterParams.search}
          />

          <div className="flex items-center justify-start gap-2">
            <ListFilters
              filters={[
                {
                  className: 'min-w-40',
                  onChange: (product_category) => setFilterParams((prev) => ({ ...prev, product_category, page: null })),
                  onInputChange: setSearchProductCategories,
                  value: filterParams.product_category,
                  options: [{ label: 'Todas', value: '' }, ...productCategoryOptions],
                  placeholder: 'Serviços'
                },
              ]}
            />
          </div>
        </div>

        <Table
          columns={tefConfigurationProductsTableColumns(tefProvider)}
          isLoadingData={tefProductCodesIsPending}
          rows={tefProductCodes || []}
          totalPages={tefProductCodesMeta.total_pages}
          totalCount={tefProductCodesMeta.total}
          hasPagination
          page={Number(filterParams.page) || 1}
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          rowEmptyValue="--"
        />
      </div>
    </ErrorMessageWrapper>
  );
}

export default TefConfigurationShow;
